<template>
  <div class="pros-carousel">
    <ProductCarousel
      v-if="getProductIds"
      :display-divider="isDividerNeeded"
      :display-sale-price-only="!isMcom"
      :edit-products="editProducts"
      :filter-fetched-products="filterFetchedProducts"
      :id-selected-color-map="idSelectedColorMap"
      :is-simplification-on="configData.isSimplificationOn"
      :override-price-simplification="configData.isSimplificationOn"
      :simplification-with-v1="configData.simplificationWithV1"
      :simplification-non-bold-percentage-exp="configData.simplificationNonBoldPercentageExp"
      :simplification-remove-sale-exp="configData.simplificationRemoveSaleExp"
      :suppress-tool-tip="true"
      :image-width="getImageWidth"
      :link-name="linkName"
      :link-url="linkUrl"
      :number-of-items="numberOfItems"
      :show-review-condensed="showReviewCondensed"
      :color-swatch-enhancement="colorSwatchEnhancement"
      :device-type="deviceType"
      :open-in-new-tab="configData.openInNewTab"
      :picture-skeleton="true"
      :product-xapi-context-param="urlParams"
      :productids="getProductIds"
      :products="configData.recommendationData?.productCollection || []"
      :restructure-product="transformer"
      :simplified-thumbnail="configData.simplifiedThumbnail"
      :show-bops-only-products="configData.showBopsOnlyProducts"
      :show-corner-badge="isShowCornerBadge"
      :title="title"
      :use-wish-list="useWishList"
      :wrap-product-name="true"
      @link-click-event="linkClickEvent"
      @wish-list-icon-clicked="wishListIconClicked"
      @panel-view-event="contentViewEvent"
      @pixel-presented-event="(e) => fireAnalyticsEvent('PixelPresented', e)"
      @scroll-event="(e) => fireAnalyticsEvent('scrolled', e)"
      @removeProductsEvent="(e) => $emit('removeProductsEvent', e)"
      @rviDoneEvent="(e, carouselProductLength) => {
        setRviSingleItemTitle(carouselProductLength);
        $emit('rviDoneEvent', e);
      }"
      @rviEditEvent="(e) => $emit('rviEditEvent', e)"
      @viewAllEvent="(e) => contentLinkClickEvent(e)"
      @dataLoadStatusEvent="(e) => $emit('dataLoadStatusEvent', e)"
    >
      <template #title>
        <component
          :is="headerType"
          :tabindex="configData.headerTabIndex"
          :aria-label="configData.headerAriaLabel"
        >
          {{ title }}
        </component>
      </template>
    </ProductCarousel>
  </div>
</template>

<script>
import { ProductCarousel } from '@common-vue/product-carousel';
import {
  getContextUrlParams,
  getProductThumbnailSize,
  getRequester,
  isMcom,
  getProductIdsWithPos,
} from '../../utils/utils';
import constants from '../../utils/constants';
import {
  linkClickEventTag,
  contentViewEventTag,
  firePixelOrScrollEvent,
  contentlinkClickEventTag, fireHeartIconClickedTag,
} from '../../analytics/analyticsEvent';
import { productTransformerWithTDPFactory } from '../../utils/productTransformer';

const { SMALL, MEDIUM, LARGE } = constants.BREAK_POINT_TYPE;

export default {
  name: 'ProsCarousel',
  components: { ProductCarousel },
  props: {
    configData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: [
    'dataLoadStatusEvent',
    'removeProductsEvent',
    'rviDoneEvent',
    'rviEditEvent',
  ],
  setup() {
    const entryPoint = process.env.ENTRY_POINT;
    const requester = getRequester(entryPoint);
    return { requester, isMcom: isMcom() };
  },
  data() {
    return {
      analyticsFired: [],
      isShowCornerBadge: this.configData.showCornerBadge,
      numberOfItems: this.configData.numberOfItems || {
        [SMALL]: 2.5,
        [MEDIUM]: 5.5,
        [LARGE]: 5.25,
      },
      customHeaderType: this.configData.customHeaderType,
      title: this.configData.title || '',
      useWishList: this.configData.useWishList,
      linkName: this.configData.linkName || '',
      linkUrl: this.configData.linkUrl || '',
      editProducts: this.configData.editProducts,
      filterFetchedProducts: this.configData.filterFetchedProducts,
      showReviewCondensed: this.configData.showReviewCondensed,
      colorSwatchEnhancement: this.configData.colorSwatchEnhancement,
      deviceType: this.configData.deviceType ? 'PHONE' : '',
    };
  },
  computed: {
    getImageWidth() {
      return getProductThumbnailSize(this.numberOfItems);
    },
    getProductIds() {
      if (this.configData?.recommendationData?.productCollection?.length > 0) {
        return [];
      }

      return (
        this.configData.productIds
        || this.configData.recommendationData?.recommendedIdsList?.map((i) => i.id)
      );
    },
    urlParams() {
      return getContextUrlParams(this.configData);
    },
    isDividerNeeded() {
      return this.configData.showDivider
        ? this.configData.showDivider.value
        : ['SB_ZONE_A', 'SB_RVI'].includes(this.configData.zone);
    },
    headerType() {
      return this.customHeaderType ?? 'h3';
    },
    idSelectedColorMap() {
      return (
        this.configData.recommendationData?.displayColorImage
        && this.configData.recommendationData?.recommendedIdsList?.reduce(
          (acc, { id, colorName }) => {
            acc[id] = colorName;
            return acc;
          },
          {},
        )
      );
    },
    transformer() {
      return productTransformerWithTDPFactory(
        {
          requester: this.requester,
          zone: this.configData?.zone,
          choiceId: this.configData?.recommendationData?.encodedChoiceId,
        },
        this.configData?.transformer,
        this.configData?.recommendationConfig?.selectedColorFamily,
        this.configData?.enableColorRecommendations,
      );
    },
  },
  methods: {
    linkClickEvent(productId, position) {
      linkClickEventTag(
        productId,
        position,
        this.configData?.recommendationData,
        this.configData?.zone,
        this.configData?.impressionData,
      );
    },
    contentViewEvent() {
      contentViewEventTag(
        this.configData?.recommendationData,
        this.configData.zone,
        this.configData.impressionData || {
          media_type: 'PROS:standard panels',
          content_position: 'R00S00',
        },
        this.configData.properties,
      );
    },
    contentLinkClickEvent(linkName) {
      if (linkName !== 'Edit' && linkName !== 'Done') {
        contentlinkClickEventTag(
          this.configData.recommendationData,
          this.configData.zone,
        );
      }
    },
    setRviSingleItemTitle(carouselProductLength = 0) {
      if (carouselProductLength === 1) {
        this.title = this.title.replace('items', 'item');
      }
    },
    fireAnalyticsEvent(name, e) {
      const pList = getProductIdsWithPos(e);
      const trackAnalyticsFired = `${name}${this.configData.zone}${this.requester}${pList}`;

      if (this.analyticsFired.includes(trackAnalyticsFired)) {
        return;
      }

      firePixelOrScrollEvent({
        name,
        zone: this.configData.zone,
        requester: this.requester,
        pList,
        recProductIds: this.getProductIds,
        encodedChoiceId: this.configData?.recommendationData?.encodedChoiceId,
        contentId: this.configData?.recommendationData?.content?.contentId,
      });

      this.analyticsFired.push(trackAnalyticsFired);
    },
    wishListIconClicked(data = {}) {
      const { isInWishlist, productId, position } = data;
      fireHeartIconClickedTag(isInWishlist, position, productId, this.configData.zone, this.title, this.configData.recommendationData);
    },
  },
};
</script>

<style lang="scss" scoped>
.pros-collection {
  .sb-zone-a {
    margin-bottom: -8px;
    margin-top: 16px;

    @media screen and (max-width: 64rem) {
      margin-right: -20px;
    }
  }
}
</style>
